<template>
  <div class="create-job-page">
    <div class="page-header">
      <h1 class="page-title">공고 등록</h1>
      <p class="page-subtitle">분양 현장 정보를 입력해주세요</p>
    </div>

    <form @submit.prevent="handleSubmit" class="job-form">
      <section class="form-section">
        <h2 class="section-title">기본 정보</h2>
        
        <div class="input-group">
          <label for="title">공고 제목 <span class="required">*</span></label>
          <input 
            type="text" 
            id="title" 
            v-model="form.title" 
            placeholder="예: 강남 힐스테이트 분양팀 팀원 모집"
            required
          />
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="siteName">현장명 <span class="required">*</span></label>
            <input 
              type="text" 
              id="siteName" 
              v-model="form.siteName" 
              placeholder="예: 힐스테이트 강남"
              required
            />
          </div>
          <div class="input-group">
            <label for="startDate">투입일 <span class="required">*</span></label>
            <input 
              type="date" 
              id="startDate" 
              v-model="form.startDate"
              required
            />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="deadline">마감일</label>
            <input 
              type="date" 
              id="deadline" 
              v-model="form.deadline"
              placeholder="모집 마감일"
            />
          </div>
          <div class="input-group">
            <label>&nbsp;</label>
            <div class="deadline-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>마감일을 설정하면 D-Day가 표시됩니다</span>
            </div>
          </div>
        </div>

        <div class="input-group">
          <label for="address">현장 주소 <span class="required">*</span></label>
          <div class="address-search-wrapper">
            <div class="address-input-box">
              <input 
                type="text" 
                id="address" 
                v-model="form.address" 
                placeholder="주소 검색을 클릭하여 주소를 입력해주세요"
                readonly
                @click="openAddressSearch"
                required
              />
              <button type="button" class="btn-search" @click="openAddressSearch">
                주소 검색
              </button>
            </div>
            <input 
              type="text" 
              v-model="form.detailAddress" 
              placeholder="상세 주소 (선택사항)"
              class="detail-address-input"
            />
          </div>

          <div v-if="form.address" class="map-preview-area">
            <KakaoMap 
              :address="form.address" 
              :markerTitle="form.siteName || '현장 위치'"
              height="300px"
            />
          </div>
        </div>
        
        <div class="input-row">
          <div class="input-group">
            <label for="region">지역 <span class="required">*</span></label>
            <select id="region" v-model="form.region" required>
              <option value="">선택하세요</option>
              <option v-for="region in regions" :key="region" :value="region">{{ region }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="industry">업종 <span class="required">*</span></label>
            <select id="industry" v-model="form.industry" required>
              <option value="">선택하세요</option>
              <option v-for="industry in industries" :key="industry" :value="industry">{{ industry }}</option>
            </select>
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">회사 정보</h2>
        <div class="input-row">
          <div class="input-group">
            <label for="constructor">시공사</label>
            <input type="text" id="constructor" v-model="form.constructor" placeholder="예: 현대건설" />
          </div>
          <div class="input-group">
            <label for="developer">시행사</label>
            <input type="text" id="developer" v-model="form.developer" placeholder="예: ○○디벨롭먼트" />
          </div>
        </div>
        <div class="input-row">
          <div class="input-group">
            <label for="agency">대행사</label>
            <input type="text" id="agency" v-model="form.agency" placeholder="예: ○○분양대행" />
          </div>
          <div class="input-group">
            <label for="trust">신탁사</label>
            <input type="text" id="trust" v-model="form.trust" placeholder="예: ○○신탁" />
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">모집 정보</h2>
        <div class="input-row">
          <div class="input-group">
            <label for="jobType">직종 <span class="required">*</span></label>
            <select id="jobType" v-model="form.jobType" required>
              <option value="">선택하세요</option>
              <option v-for="type in jobTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="headcount">모집인원</label>
            <input type="text" id="headcount" v-model="form.headcount" placeholder="예: 00명" />
          </div>
        </div>
        <div class="input-row">
          <div class="input-group">
            <label for="gender">성별</label>
            <select id="gender" v-model="form.gender">
              <option v-for="gender in genders" :key="gender" :value="gender">{{ gender }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="age">연령</label>
            <input type="text" id="age" v-model="form.age" placeholder="예: 20세 이상" />
          </div>
        </div>
        <div class="input-group">
          <label for="experience">경력</label>
          <select id="experience" v-model="form.experience">
            <option v-for="exp in experiences" :key="exp" :value="exp">{{ exp }}</option>
          </select>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">급여 조건</h2>
        <div class="input-row">
          <div class="input-group">
            <label for="salaryType">급여 유형 <span class="required">*</span></label>
            <select id="salaryType" v-model="form.salaryType" required>
              <option value="">선택하세요</option>
              <option v-for="type in salaryTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="salary">급여 <span class="required">*</span></label>
            <input 
              type="text" 
              id="salary" 
              v-model="form.salary" 
              placeholder="예: 300만원~, 협의"
              required
            />
          </div>
        </div>
        <div class="input-row">
          <div class="input-group">
            <label for="transportationFee">교통비</label>
            <input type="text" id="transportationFee" v-model="form.transportationFee" placeholder="예: 별도 지급, 포함" />
          </div>
          <div class="input-group">
            <label for="salesFee">영업비</label>
            <input type="text" id="salesFee" v-model="form.salesFee" placeholder="예: 별도 지급, 포함" />
          </div>
        </div>
        <div class="input-group">
          <label for="benefits">근무 후생</label>
          <input type="text" id="benefits" v-model="form.benefits" placeholder="예: 4대보험, 중식제공, 차량지원" />
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">현장 이미지</h2>
        <div class="image-upload">
          <input 
            type="file" 
            id="thumbnail" 
            ref="fileInput"
            accept="image/*"
            @change="handleImageChange"
            hidden
          />
          <div v-if="!imagePreview" class="upload-placeholder" @click="$refs.fileInput.click()">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" stroke-width="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <p>클릭하여 이미지 업로드</p>
            <span>권장 사이즈: 640 x 360 (16:9)</span>
          </div>
          <div v-else class="image-preview">
            <img :src="imagePreview" alt="미리보기" />
            <button type="button" class="remove-btn" @click="removeImage">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">상세 내용</h2>
        <div class="input-group">
          <label for="description">상세 설명</label>
          <textarea 
            id="description" 
            v-model="form.description" 
            rows="8"
            placeholder="근무 조건, 우대 사항, 기타 안내 사항 등을 자유롭게 작성해주세요."
          ></textarea>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">담당자 정보</h2>
        <div class="input-row">
          <div class="input-group">
            <label for="contactName">담당자명 <span class="required">*</span></label>
            <input type="text" id="contactName" v-model="form.contactName" placeholder="예: 홍길동" required />
          </div>
          <div class="input-group">
            <label for="contactPhone">연락처 <span class="required">*</span></label>
            <input type="tel" id="contactPhone" v-model="form.contactPhone" placeholder="예: 010-1234-5678" required />
          </div>
        </div>
        <div class="input-row">
          <div class="input-group">
            <label for="siteUrl">사이트 주소</label>
            <input type="url" id="siteUrl" v-model="form.siteUrl" placeholder="https://" />
          </div>
          <div class="input-group">
            <label for="snsUrl">SNS 링크</label>
            <input type="url" id="snsUrl" v-model="form.snsUrl" placeholder="https://" />
          </div>
        </div>
      </section>

      <!-- 광고 옵션 섹션 -->
      <JobAdOptionsSection 
        v-model="selectedAdOptions"
        @total-change="adTotalPrice = $event"
      />

      <p v-if="submitError" class="error-message">{{ submitError }}</p>

      <!-- 결제 요약 바 (광고 옵션 선택 시) -->
      <div v-if="selectedAdOptions.length > 0" class="payment-summary-bar">
        <div class="summary-info">
          <span class="summary-label">광고 옵션 {{ selectedAdOptions.length }}개 선택</span>
          <span class="summary-price">{{ formatPrice(adTotalPrice) }}P</span>
        </div>
        <div v-if="adTotalPrice > userPoints" class="insufficient-notice">
          포인트 부족 ({{ formatPrice(adTotalPrice - userPoints) }}P 더 필요)
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn-secondary" @click="$router.back()">취소</button>
        <button 
          type="submit" 
          class="btn-primary" 
          :disabled="isSubmitting || (selectedAdOptions.length > 0 && adTotalPrice > userPoints)"
        >
          {{ isSubmitting ? '등록 중...' : submitButtonText }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { doc, collection, serverTimestamp, runTransaction } from 'firebase/firestore'
import { db } from '@/services/firebase'
import { useAuth } from '@/composables/useAuth'
import { useJobs, JOB_CONSTANTS } from '@/composables/useJobs'
import { useProducts } from '@/composables/useProducts'
import KakaoMap from '@/components/KakaoMap.vue'
import JobAdOptionsSection from '@/components/JobAdOptionsSection.vue'

export default {
  name: 'CreateJobView',
  components: {
    KakaoMap,
    JobAdOptionsSection
  },
  setup() {
    const router = useRouter()
    const { user, userProfile, refreshProfile } = useAuth()
    const { createJob, uploadImage } = useJobs()
    const { loadProducts, getProduct, formatPrice, calculateExpiry } = useProducts()

    const fileInput = ref(null)
    const imageFile = ref(null)
    const imagePreview = ref(null)
    const isSubmitting = ref(false)
    const submitError = ref(null)

    // 광고 옵션
    const selectedAdOptions = ref([])
    const adTotalPrice = ref(0)

    const form = reactive({
      title: '',
      siteName: '',
      startDate: '',
      deadline: '',
      address: '',
      detailAddress: '',
      region: '',
      industry: '',
      constructor: '',
      developer: '',
      agency: '',
      trust: '',
      jobType: '',
      headcount: '',
      gender: '무관',
      age: '',
      experience: '무관',
      salaryType: '',
      salary: '',
      transportationFee: '',
      salesFee: '',
      benefits: '',
      description: '',
      contactName: '',
      contactPhone: '',
      siteUrl: '',
      snsUrl: '',
      thumbnail: ''
    })

    const { regions, industries, jobTypes, salaryTypes, genders, experiences } = JOB_CONSTANTS

    // 보유 포인트
    const userPoints = computed(() => userProfile.value?.points || 0)

    // 제출 버튼 텍스트
    const submitButtonText = computed(() => {
      if (selectedAdOptions.value.length > 0) {
        return `공고 등록하기 (${formatPrice(adTotalPrice.value)}P)`
      }
      return '공고 등록하기'
    })

    const loadPostcodeScript = () => {
      const script = document.createElement('script')
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      document.head.appendChild(script)
    }

    onMounted(async () => {
      loadPostcodeScript()
      await loadProducts()
    })

    const openAddressSearch = () => {
      if (!window.daum || !window.daum.Postcode) {
        alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.')
        return
      }

      new window.daum.Postcode({
        oncomplete: (data) => {
          let addr = ''
          if (data.userSelectedType === 'R') {
            addr = data.roadAddress
          } else {
            addr = data.jibunAddress
          }
          form.address = addr
        }
      }).open()
    }

    const handleImageChange = (e) => {
      const file = e.target.files[0]
      if (file) {
        imageFile.value = file
        imagePreview.value = URL.createObjectURL(file)
      }
    }

    const removeImage = () => {
      imageFile.value = null
      imagePreview.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const handleSubmit = async () => {
      if (!user.value) {
        router.push('/login')
        return
      }

      // 포인트 부족 체크
      if (selectedAdOptions.value.length > 0 && adTotalPrice.value > userPoints.value) {
        submitError.value = '포인트가 부족합니다. 충전 후 다시 시도해주세요.'
        return
      }

      isSubmitting.value = true
      submitError.value = null

      try {
        // 이미지 업로드
        if (imageFile.value) {
          form.thumbnail = await uploadImage(imageFile.value)
        }

        // 공고 데이터 준비
        const jobData = {
          ...form,
          userId: user.value.uid,
          // 기본값
          isPremium: false,
          isPick: false,
          isUrgent: false,
          isFeatured: false,
          viewCount: 0,
          bookmarkCount: 0,
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }

        // 광고 옵션이 있는 경우 트랜잭션으로 처리
        if (selectedAdOptions.value.length > 0) {
          await runTransaction(db, async (transaction) => {
            // 포인트 차감
            const userRef = doc(db, 'users', user.value.uid)
            const userSnap = await transaction.get(userRef)
            const currentPoints = userSnap.data().points || 0

            if (currentPoints < adTotalPrice.value) {
              throw new Error('포인트 부족')
            }

            // 공고 생성
            const jobRef = doc(collection(db, 'jobs'))
            
            // 선택된 광고 옵션 적용
            const bannerProductsList = []

            for (const productId of selectedAdOptions.value) {
              const product = getProduct(productId)
              if (!product) continue

              if (product.category === 'job') {
                // 공고 업그레이드
                jobData[product.field] = true
                jobData[`${product.field}Until`] = calculateExpiry(product.duration)
              } else if (product.category === 'banner') {
                bannerProductsList.push(product)
              }
            }

            // 공고 저장
            transaction.set(jobRef, jobData)

            // 배너 광고 생성 (공고 정보 기반)
            for (const product of bannerProductsList) {
              const bannerRef = doc(collection(db, product.collection))
              const bannerData = {
                userId: user.value.uid,
                jobId: jobRef.id,
                link: `/jobs/${jobRef.id}`,
                isActive: true,
                expiresAt: calculateExpiry(product.duration),
                order: 999, // 맨 뒤에 추가
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              }

              // 배너 타입별 데이터
              if (['topBanners', 'subBanners'].includes(product.collection)) {
                bannerData.text = `🔥 ${form.siteName || form.title} - 지금 확인하세요!`
                bannerData.bgColor = product.color || '#3182f6'
              } else {
                bannerData.title = form.siteName || form.title
                bannerData.image = form.thumbnail || ''
                bannerData.imageUrl = form.thumbnail || ''
              }

              transaction.set(bannerRef, bannerData)
            }

            // 포인트 차감
            transaction.update(userRef, {
              points: currentPoints - adTotalPrice.value,
              updatedAt: serverTimestamp()
            })

            // 포인트 내역 기록
            const txRef = doc(collection(db, 'pointTransactions'))
            const productNames = selectedAdOptions.value.map(id => getProduct(id)?.name).filter(Boolean).join(', ')
            transaction.set(txRef, {
              userId: user.value.uid,
              type: 'spend',
              amount: -adTotalPrice.value,
              balance: currentPoints - adTotalPrice.value,
              reason: 'product_purchase',
              description: `공고 등록 광고 옵션: ${productNames}`,
              jobId: jobRef.id,
              createdAt: serverTimestamp()
            })

            // 구매 기록
            for (const productId of selectedAdOptions.value) {
              const product = getProduct(productId)
              if (!product) continue

              const purchaseRef = doc(collection(db, 'purchases'))
              transaction.set(purchaseRef, {
                userId: user.value.uid,
                productId: product.id,
                productName: product.name,
                price: product.price,
                duration: product.duration,
                targetType: product.category === 'job' ? 'job' : 'banner',
                targetId: jobRef.id,
                expiresAt: calculateExpiry(product.duration),
                status: 'active',
                createdAt: serverTimestamp()
              })
            }

            // 성공 시 프로필 새로고침 및 이동
            await refreshProfile()
            router.push(`/jobs/${jobRef.id}`)
          })
        } else {
          // 광고 옵션 없이 일반 등록
          const jobId = await createJob(form, user.value.uid)
          router.push(`/jobs/${jobId}`)
        }
      } catch (e) {
        console.error('공고 등록 실패:', e)
        submitError.value = e.message || '공고 등록에 실패했습니다. 다시 시도해주세요.'
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      form,
      regions,
      industries,
      jobTypes,
      salaryTypes,
      genders,
      experiences,
      fileInput,
      imagePreview,
      isSubmitting,
      submitError,
      selectedAdOptions,
      adTotalPrice,
      userPoints,
      submitButtonText,
      handleImageChange,
      removeImage,
      handleSubmit,
      openAddressSearch,
      formatPrice
    }
  }
}
</script>

<style scoped>
.create-job-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.page-header {
  margin-bottom: 40px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
}

.job-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  background: #fff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f2f4f6;
}

.input-group {
  margin-bottom: 20px;
}

.input-group:last-child {
  margin-bottom: 0;
}

.input-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #e03131;
}

.input-group input,
.input-group select,
.input-group textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.15s;
  background: #fff;
}

.input-group input:focus,
.input-group select:focus,
.input-group textarea:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.input-group input::placeholder,
.input-group textarea::placeholder {
  color: #adb5bd;
}

.input-group textarea {
  resize: vertical;
  min-height: 120px;
}

.input-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.deadline-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f0f7ff;
  border-radius: 10px;
  height: 50px;
}

.deadline-hint svg {
  color: #3182f6;
  flex-shrink: 0;
}

.deadline-hint span {
  font-size: 13px;
  color: #1971c2;
  font-weight: 500;
}

.address-search-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.address-input-box {
  display: flex;
  gap: 8px;
}

.address-input-box input {
  flex: 1;
  cursor: pointer;
}

.btn-search {
  padding: 0 16px;
  height: 50px;
  background: #333;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.btn-search:hover {
  background: #191f28;
}

.detail-address-input {
  margin-top: 4px;
}

.map-preview-area {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
}

.image-upload {
  margin-top: 8px;
}

.upload-placeholder {
  border: 2px dashed #e5e8eb;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
}

.upload-placeholder:hover {
  border-color: #3182f6;
  background: #f9fafb;
}

.upload-placeholder p {
  font-size: 15px;
  font-weight: 500;
  color: #4e5968;
  margin: 16px 0 4px;
}

.upload-placeholder span {
  font-size: 13px;
  color: #8b95a1;
}

.image-preview {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.remove-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.payment-summary-bar {
  background: #f0f7ff;
  border: 1px solid #3182f6;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-label {
  font-size: 14px;
  color: #4e5968;
}

.summary-price {
  font-size: 20px;
  font-weight: 700;
  color: #3182f6;
}

.insufficient-notice {
  font-size: 13px;
  color: #ef4444;
  font-weight: 500;
}

.error-message {
  color: #e03131;
  font-size: 14px;
  text-align: center;
  padding: 16px;
  background: #fff5f5;
  border-radius: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-primary {
  padding: 16px 32px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #1b64da;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 16px 32px;
  background: #f2f4f6;
  color: #333;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: #e5e8eb;
}

@media (max-width: 768px) {
  .create-job-page {
    padding: 24px 16px 60px;
  }

  .page-title {
    font-size: 24px;
  }

  .form-section {
    padding: 24px;
  }

  .input-row {
    grid-template-columns: 1fr;
  }

  .deadline-hint {
    margin-top: 8px;
  }

  .payment-summary-bar {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>